import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/confirm-email")({
  head: () => ({
    meta: [
      { title: "Confirm your email — KnowMeno" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ConfirmEmailPage,
});

const RO = "#D4537E";
const GR = "#888780";
const PLUM = "#4A1942";
const WH = "#ffffff";

function ConfirmEmailPage() {
  const [status, setStatus] = useState<"pending" | "ok" | "failed">("pending");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = new URL(window.location.href);
      const tokenHash = url.searchParams.get("token_hash");
      const type = url.searchParams.get("type") || "signup";
      if (!tokenHash) {
        setError("This confirmation link is missing a token.");
        setStatus("failed");
        return;
      }
      const { error: vErr } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type as any,
      });
      if (cancelled) return;
      if (vErr) {
        setError(vErr.message);
        setStatus("failed");
      } else {
        setStatus("ok");
        setTimeout(() => navigate({ to: "/app" }), 1200);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(165deg, #FBFAF6 0%, #F7EEEA 60%, #F2E1DA 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        fontFamily: '"Cormorant Garamond", Georgia, serif',
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: "100%",
          background: WH,
          borderRadius: 24,
          padding: "36px 32px",
          textAlign: "center",
          boxShadow: "0 30px 80px -30px rgba(74, 25, 66, 0.25)",
        }}
      >
        <div
          style={{
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            display: "inline-flex",
            alignItems: "baseline",
            marginBottom: 16,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <span style={{ color: RO }}>Meno</span>
          <span style={{ color: GR, fontWeight: 300, margin: "0 1px" }}>-</span>
          <span style={{ color: PLUM }}>IQ</span>
        </div>
        {status === "pending" && (
          <p
            style={{
              color: GR,
              fontSize: 15,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            Confirming your email…
          </p>
        )}
        {status === "ok" && (
          <>
            <h1
              style={{
                fontSize: 30,
                lineHeight: 1.15,
                color: "#2A1228",
                marginBottom: 10,
                fontWeight: 500,
              }}
            >
              You're all set.
            </h1>
            <p
              style={{
                color: GR,
                fontSize: 14,
                fontFamily: "system-ui, sans-serif",
              }}
            >
              Taking you to the app…
            </p>
          </>
        )}
        {status === "failed" && (
          <>
            <h1
              style={{
                fontSize: 26,
                lineHeight: 1.15,
                color: "#2A1228",
                marginBottom: 10,
                fontWeight: 500,
              }}
            >
              We couldn't confirm this link.
            </h1>
            <p
              style={{
                color: GR,
                fontSize: 14,
                fontFamily: "system-ui, sans-serif",
                marginBottom: 18,
              }}
            >
              {error || "It may have expired."}
            </p>
            <Link
              to="/sign-in"
              style={{
                background: PLUM,
                color: WH,
                padding: "12px 22px",
                borderRadius: 12,
                textDecoration: "none",
                fontFamily: "system-ui, sans-serif",
                fontSize: 14,
                display: "inline-block",
              }}
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
